import React, { useState, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import SideMenu from '../navbar/SideMenu';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  CssBaseline,
  Drawer,
  Button,
  useTheme,
  styled,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  LinearProgress,
  Stack,
  Divider
} from '@mui/material';

import {
  ExpandMore,
  Menu as MenuIcon,
  LocationOn,
  TrendingUp,
  Group,
  Woman,
  Man,
  CompareArrows,
  BarChart,
  PieChart
} from '@mui/icons-material';

const drawerWidth = 240;
const primaryColor = '#E30613';
const secondaryColor = '#008D36';
const backgroundColor = '#f5f5f5';

const MainContainer = styled('div')({
  display: 'flex',
  backgroundColor: backgroundColor,
  minHeight: '100vh',
  flexDirection: 'column'
});

const MainContentWithDrawer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  marginTop: '64px',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    marginTop: '56px'
  }
}));

const ContentWrapper = styled('main')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
  backgroundColor: backgroundColor,
  height: 'calc(100vh - 64px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    height: 'calc(100vh - 56px)'
  }
}));

const DrawerWrapper = styled('div')(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  overflow: 'auto',
  height: 'calc(100vh - 64px)',
  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
  backgroundColor: '#fff',
  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    zIndex: 1300,
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    '&.drawerOpen': {
      transform: 'translateX(0)',
    },
    '&.drawerClose': {
      transform: 'translateX(-100%)',
    },
  }
}));

const StyledTableHead = styled(TableHead)({
  backgroundColor: primaryColor,
  '& th': {
    color: 'white',
    fontWeight: 'bold'
  }
});

const StatCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4]
  }
}));

const ProgressBarWithLabel = ({ value, color, label, valueLabel }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2" fontWeight="bold">
        {valueLabel}
      </Typography>
    </Box>
    <LinearProgress 
      variant="determinate" 
      value={value} 
      sx={{ 
        height: 10, 
        borderRadius: 5,
        backgroundColor: '#e0e0e0',
        '& .MuiLinearProgress-bar': {
          backgroundColor: color,
        }
      }} 
    />
  </Box>
);


const populationData = {
  title: "DONNÉES DÉMOGRAPHIQUES DU BURUNDI",
  years: {
    "2008": {
      femmes: 4111751,
      hommes: 3926867,
      total: 8038618,
      pourcentageFemmes: 51.1,
      pourcentageHommes: 48.9
    },
    "2024": {
      femmes: 6431719,
      hommes: 5901069,
      total: 12332788,
      pourcentageFemmes: 52.4,
      pourcentageHommes: 47.6
    }
  },
  indicateurs: {
    rapportMasculinite2008: 95,
    rapportMasculinite2024: 91.7,
    densite2008: 310,
    densite2024: 489,
    tauxCroissance: 2.7,
    populationRurale: {
      effectif: 9230468,
      pourcentage: 75.5
    },
    populationUrbaine: {
      effectif: 2990466,
      pourcentage: 24.5
    },
    moins15ans: {
      effectif: 5505806,
      pourcentage: 45.5
    },
    entre15et59ans: {
      effectif: 6027615,
      pourcentage: 49.3
    },
    plus60ans: {
      effectif: 687513,
      pourcentage: 5.6
    },
    rapportDependance: 102.7,
    population18ansPlus: {
      effectif: 5858803,
      pourcentage: 47.9
    },
    moins5ans: {
      effectif: 2041065,
      pourcentage: 8.3
    },
    entre5et14ans: {
      effectif: 3464741,
      pourcentage: 28.4
    },
    entre15et24ans: {
      effectif: 2339899,
      pourcentage: 19.1
    },
    entre25et44ans: {
      effectif: 2741767,
      pourcentage: 22.4
    },
    entre45et59ans: {
      effectif: 945949,
      pourcentage: 7.7
    },
    femmes10a49ans: {
      effectif: 3790561,
      pourcentage: 31.0
    },
    femmes15a49ans: {
      effectif: 2961972,
      pourcentage: 24.2
    },
    populationScolarisable: {
      effectif: 3065878,
      pourcentage: 25.1
    },
    tailleMoyenneMenage: 4.5,
    menagesAgricoles: {
      effectif: 2335995,
      pourcentage: 95.7
    },
    tailleMoyenneMenageAgricole: 4.6
  }
};

const PopulationDataPageBurundi = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openKeyStats, setOpenKeyStats] = useState(true);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <MainContainer>
      <CssBaseline />
      <NavigationBar />
      <MainContentWithDrawer>
        {!isMobile ? (
          <DrawerWrapper>
            <SideMenu />
          </DrawerWrapper>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            <SideMenu />
          </Drawer>
        )}

        <ContentWrapper>
          <Container maxWidth="xl">
            {isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<MenuIcon />} 
                  onClick={handleDrawerToggle}
                  sx={{ color: primaryColor, borderColor: primaryColor }}
                >
                  Menu
                </Button>
              </Box>
            )}
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: { xs: 1, sm: 3 }, mb: 3, borderLeft: `4px solid ${primaryColor}`, background: 'linear-gradient(to right, #ffffff, #f9f9f9)' }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" color="primary" sx={{ mb: { xs: 1, sm: 0 } }}>
                      {populationData.title}
                    </Typography>
                    <Chip 
                      icon={<BarChart />} 
                      label={`${populationData.years["2024"].total.toLocaleString()} habitants`} 
                      color="primary" 
                      sx={{ backgroundColor: primaryColor, color: 'white', fontSize: '1rem', p: 2 }}
                    />
                  </Box>
                  
                  {/* Section des indicateurs clés */}
                  <Accordion expanded={openKeyStats} onChange={() => setOpenKeyStats(!openKeyStats)} sx={{ mb: 3, boxShadow: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TrendingUp color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                          Indicateurs Démographiques Clés
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6} lg={3}>
                          <StatCard sx={{ borderLeft: `4px solid ${primaryColor}` }}>
                            <CardContent>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Woman fontSize="small" sx={{ color: primaryColor }} />
                                <Typography variant="subtitle2" color="text.secondary">
                                  Population féminine
                                </Typography>
                              </Stack>
                              <Typography variant="h4" fontWeight="bold" color="primary">
                                {populationData.years["2024"].femmes.toLocaleString()}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {populationData.years["2024"].pourcentageFemmes}% de la population totale
                              </Typography>
                            </CardContent>
                          </StatCard>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <StatCard sx={{ borderLeft: `4px solid ${secondaryColor}` }}>
                            <CardContent>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Man fontSize="small" sx={{ color: secondaryColor }} />
                                <Typography variant="subtitle2" color="text.secondary">
                                  Population masculine
                                </Typography>
                              </Stack>
                              <Typography variant="h4" fontWeight="bold" color="secondary">
                                {populationData.years["2024"].hommes.toLocaleString()}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {populationData.years["2024"].pourcentageHommes}% de la population totale
                              </Typography>
                            </CardContent>
                          </StatCard>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <StatCard sx={{ borderLeft: `4px solid ${primaryColor}` }}>
                            <CardContent>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <CompareArrows fontSize="small" sx={{ color: primaryColor }} />
                                <Typography variant="subtitle2" color="text.secondary">
                                  Rapport de masculinité
                                </Typography>
                              </Stack>
                              <Typography variant="h4" fontWeight="bold">
                                {populationData.indicateurs.rapportMasculinite2024}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                hommes pour 100 femmes
                              </Typography>
                            </CardContent>
                          </StatCard>
                        </Grid>
                        
                        <Grid item xs={12} md={6} lg={3}>
                          <StatCard sx={{ borderLeft: `4px solid ${secondaryColor}` }}>
                            <CardContent>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <LocationOn fontSize="small" sx={{ color: secondaryColor }} />
                                <Typography variant="subtitle2" color="text.secondary">
                                  Densité de population
                                </Typography>
                              </Stack>
                              <Typography variant="h4" fontWeight="bold">
                                {populationData.indicateurs.densite2024}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                habitants/km²
                              </Typography>
                            </CardContent>
                          </StatCard>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                  
              
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, boxShadow: 2, borderLeft: `4px solid ${primaryColor}`, height: '100%' }}>
                        <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Group sx={{ mr: 1 }} /> Pyramide des âges 
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <ProgressBarWithLabel 
                          value={populationData.indicateurs.moins15ans.pourcentage} 
                          color={primaryColor}
                          label="Moins de 15 ans"
                          valueLabel={`${populationData.indicateurs.moins15ans.pourcentage}% (${populationData.indicateurs.moins15ans.effectif.toLocaleString()})`}
                        />
                        
                        <ProgressBarWithLabel 
                          value={populationData.indicateurs.entre15et59ans.pourcentage} 
                          color={secondaryColor}
                          label="15-59 ans"
                          valueLabel={`${populationData.indicateurs.entre15et59ans.pourcentage}% (${populationData.indicateurs.entre15et59ans.effectif.toLocaleString()})`}
                        />
                        
                        <ProgressBarWithLabel 
                          value={populationData.indicateurs.plus60ans.pourcentage} 
                          color="#888"
                          label="60 ans et plus"
                          valueLabel={`${populationData.indicateurs.plus60ans.pourcentage}% (${populationData.indicateurs.plus60ans.effectif.toLocaleString()})`}
                        />
                        
                        <Box sx={{ mt: 2, p: 1, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                          <Typography variant="body2">
                            <strong>Rapport de dépendance:</strong> {populationData.indicateurs.rapportDependance}%
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, boxShadow: 2, borderLeft: `4px solid ${secondaryColor}`, height: '100%' }}>
                        <Typography variant="h6" gutterBottom color="secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationOn sx={{ mr: 1 }} /> Répartition géographique
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <ProgressBarWithLabel 
                          value={populationData.indicateurs.populationRurale.pourcentage} 
                          color={secondaryColor}
                          label="Population rurale"
                          valueLabel={`${populationData.indicateurs.populationRurale.pourcentage}% (${populationData.indicateurs.populationRurale.effectif.toLocaleString()})`}
                        />
                        
                        <ProgressBarWithLabel 
                          value={populationData.indicateurs.populationUrbaine.pourcentage} 
                          color={primaryColor}
                          label="Population urbaine"
                          valueLabel={`${populationData.indicateurs.populationUrbaine.pourcentage}% (${populationData.indicateurs.populationUrbaine.effectif.toLocaleString()})`}
                        />
                        
                        <Box sx={{ mt: 3 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Taille moyenne des ménages
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                  {populationData.indicateurs.tailleMoyenneMenage}
                                </Typography>
                                <Typography variant="caption">
                                  personnes
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Ménages agricoles
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                  {populationData.indicateurs.menagesAgricoles.pourcentage}%
                                </Typography>
                                <Typography variant="caption">
                                  {populationData.indicateurs.menagesAgricoles.effectif.toLocaleString()} ménages
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Tableau comparatif 2008-2024 */}
                  <TableContainer component={Paper} sx={{ mb: 3, boxShadow: 2 }}>
                    <Table>
                      <StyledTableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Année</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Femmes</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Hommes</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Rapport M/F</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Densité (hab/km²)</TableCell>
                        </TableRow>
                      </StyledTableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>2008</TableCell>
                          <TableCell align="right">{populationData.years["2008"].femmes.toLocaleString()} ({populationData.years["2008"].pourcentageFemmes}%)</TableCell>
                          <TableCell align="right">{populationData.years["2008"].hommes.toLocaleString()} ({populationData.years["2008"].pourcentageHommes}%)</TableCell>
                          <TableCell align="right">{populationData.years["2008"].total.toLocaleString()}</TableCell>
                          <TableCell align="right">{populationData.indicateurs.rapportMasculinite2008}</TableCell>
                          <TableCell align="right">{populationData.indicateurs.densite2008}</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>2024</TableCell>
                          <TableCell align="right">{populationData.years["2024"].femmes.toLocaleString()} ({populationData.years["2024"].pourcentageFemmes}%)</TableCell>
                          <TableCell align="right">{populationData.years["2024"].hommes.toLocaleString()} ({populationData.years["2024"].pourcentageHommes}%)</TableCell>
                          <TableCell align="right">{populationData.years["2024"].total.toLocaleString()}</TableCell>
                          <TableCell align="right">{populationData.indicateurs.rapportMasculinite2024}</TableCell>
                          <TableCell align="right">{populationData.indicateurs.densite2024}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Indicateurs détaillés */}
                  <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, boxShadow: 2, borderLeft: `4px solid ${primaryColor}` }}>
                        <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                          <PieChart sx={{ mr: 1 }} /> Détails par tranche d'âge 
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={1}>
                          {[
                            { label: "Moins de 5 ans", data: populationData.indicateurs.moins5ans },
                            { label: "5-14 ans", data: populationData.indicateurs.entre5et14ans },
                            { label: "15-24 ans", data: populationData.indicateurs.entre15et24ans },
                            { label: "25-44 ans", data: populationData.indicateurs.entre25et44ans },
                            { label: "45-59 ans", data: populationData.indicateurs.entre45et59ans },
                            { label: "18 ans et plus", data: populationData.indicateurs.population18ansPlus },
                          ].map((item, index) => (
                            <React.Fragment key={index}>
                              <Grid item xs={6}>
                                <Typography variant="body2">
                                  {item.label}:
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" fontWeight="bold" align="right">
                                  {item.data.effectif.toLocaleString()} ({item.data.pourcentage}%)
                                </Typography>
                              </Grid>
                            </React.Fragment>
                          ))}
                        </Grid>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, boxShadow: 2, borderLeft: `4px solid ${secondaryColor}` }}>
                        <Typography variant="h6" gutterBottom color="secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Woman sx={{ mr: 1 }} /> Population féminine spécifique
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={1}>
                          {[
                            { label: "Femmes 10-49 ans (âge de procréer)", data: populationData.indicateurs.femmes10a49ans },
                            { label: "Femmes 15-49 ans (âge de procréer)", data: populationData.indicateurs.femmes15a49ans },
                            { label: "Population scolarisable au fondamental", data: populationData.indicateurs.populationScolarisable },
                          ].map((item, index) => (
                            <React.Fragment key={index}>
                              <Grid item xs={8}>
                                <Typography variant="body2">
                                  {item.label}:
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <Typography variant="body2" fontWeight="bold" align="right">
                                  {item.data.effectif.toLocaleString()} ({item.data.pourcentage}%)
                                </Typography>
                              </Grid>
                            </React.Fragment>
                          ))}
                          <Grid item xs={8}>
                            <Typography variant="body2">
                              Taille moyenne ménage agricole:
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="body2" fontWeight="bold" align="right">
                              {populationData.indicateurs.tailleMoyenneMenageAgricole} personnes
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>
                
                {/* Notes méthodologiques */}
                <Paper sx={{ p: 2, backgroundColor: '#f0f0f0', borderLeft: `4px solid ${secondaryColor}`, boxShadow: 2 }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Notes méthodologiques
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Les données présentées sont issues du Recensement Général de la Population, de l'Habitat, de l'Agriculture et de l'Elevage (RGPHAE) 2024.
                    Les chiffres incluent uniquement la population des ménages ordinaires.
                    Le rapport de masculinité est calculé comme le nombre d'hommes pour 100 femmes.
                    Le rapport de dépendance représente le nombre de personnes de moins de 15 ans et de 60 ans et plus, prise en charge par 100 personnes de 15-59 ans.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </ContentWrapper>
      </MainContentWithDrawer>
      <Footer />
    </MainContainer>
  );
};

export default PopulationDataPageBurundi;